import {
    useMutation,
} from '@tanstack/react-query'
import {
    useCallback,
    useRef,
    useState,
} from 'react'
import {
    RequestConfigFunction,
} from 'app/types/request.types'
import simpleApi from 'app/services/simpleApi'
import {
    useJWTToken,
} from 'app/Auth'
import sleep from 'app/utils/sleep'

import {
    BatchEntity,
} from 'app/enums/common.enums'

import useBatchOperation from 'app/requests/BatchOperations/hooks/useBatchOperation'
import {
    FailedUpload,
} from '../../components/UploadFeedback/UploadFeedback.types'
import {
    WAIT_BETWEEN_REQUESTS_IN_SERIES,
} from '../../BatchActions.utils'
import {
    BatchOperationPayload,
    ResponseWithFormError,
} from './useBatchUpload.types'

type Props = {
    uploadParamsMapper: RequestConfigFunction<BatchOperationPayload<Record<string, any>>>
    plainData: Record<string, any>[]
    entity: BatchEntity
}

const useBatchUpload = ({
    uploadParamsMapper,
    plainData,
    entity,
}: Readonly<Props>) => {
    const token = useJWTToken()
    const shouldStopRef = useRef(false)

    const {
        startBatchOperation,
        completeBatchOperation,
        cancelBatchOperation,
    } = useBatchOperation({
        targetEntity: entity,
    })

    const [
        successfulUploads,
        setSuccessfulUploads,
    ] = useState(0)
    const [
        failedUploads,
        setFailedUploads,
    ] = useState<FailedUpload[]>([])

    const [
        isLoading,
        setIsLoading,
    ] = useState(false)

    const handleSuccessUpload = useCallback(() => {
        setSuccessfulUploads((prev) => {
            return prev + 1
        })
    }, [])

    const handleFailedUpload = useCallback((
        failedUpload: ResponseWithFormError,
        context: Record<string, any>,
    ) => {
        setFailedUploads((prev) => {
            return [
                ...prev,
                {
                    ...context,
                    errors: failedUpload.response.data,
                },
            ]
        })
    }, [])

    const {
        mutateAsync,
    } = useMutation({
        mutationFn: (data: BatchOperationPayload<Record<string, any>>) => {
            return simpleApi({
                ...uploadParamsMapper(data),
                token,
            })
        },
        onSuccess: handleSuccessUpload,
    })

    const uploadRecords = useCallback(async (formData?: Record<string, any>) => {
        setIsLoading(true)
        const startBatchResponse = await startBatchOperation({
            batchSize: plainData.length,
        })

        setTimeout(async () => {
            shouldStopRef.current = false

            await plainData.reduce<Promise<void>>(async (previousPromise, record, index) => {
                await previousPromise
                if (shouldStopRef.current) {
                    return
                }
                if (index > 0) {
                    await sleep(WAIT_BETWEEN_REQUESTS_IN_SERIES)
                }

                const dataToSend: BatchOperationPayload<Record<string, any>> = {
                    ...record,
                    ...(formData || {}),
                    batchOperationId: startBatchResponse.id,
                }

                try {
                    await mutateAsync(dataToSend)
                } catch (error) {
                    if (!shouldStopRef.current) {
                        handleFailedUpload(error, dataToSend)
                    }
                }
            }, Promise.resolve())

            if (!shouldStopRef.current) {
                completeBatchOperation({})
            }

            setIsLoading(false)
        })
    }, [
        completeBatchOperation,
        handleFailedUpload,
        mutateAsync,
        plainData,
        startBatchOperation,
    ])
    const stopUploadRecords = useCallback(() => {
        shouldStopRef.current = true
        cancelBatchOperation({
            processedItems: successfulUploads + failedUploads.length,
        })
    }, [
        cancelBatchOperation,
        failedUploads.length,
        successfulUploads,
    ])

    return {
        uploadRecords,
        successfulUploads,
        failedUploads,
        isLoading,
        stopUploadRecords,
    }
}

export default useBatchUpload
